import React from "react";
import { UseFormReturn } from "react-hook-form";
import { PodcastsFilterFormData } from "../../hooks/PodcastsFilterFormSchema";
import { Star } from "lucide-react";

interface ActiveFiltersHandlerProps {
  form: UseFormReturn<PodcastsFilterFormData>;
  onClearAll: () => void;
}

const ActiveFiltersHandler: React.FC<ActiveFiltersHandlerProps> = ({
  form,
  onClearAll,
}) => {
  const isFavoriteOnly = form.watch("isFavoriteOnly");
  const isFeaturedOnly = form.watch("isFeaturedOnly");
  const trades = form.watch("trades") || [];
  const categories = form.watch("categories") || [];
  const businessRoles = form.watch("businessRoles") || [];

  const hasActiveFilters =
    isFavoriteOnly ||
    isFeaturedOnly ||
    trades.length > 0 ||
    categories.length > 0 ||
    businessRoles.length > 0;

  if (!hasActiveFilters) return null;

  const handleRemoveTrade = (id: number) => {
    const updated = trades.filter((item) => item.id !== id);
    form.setValue("trades", updated);
  };

  const handleRemoveCategory = (id: number) => {
    const updated = categories.filter((item) => item.id !== id);
    form.setValue("categories", updated);
  };

  const handleRemoveEmployeeRole = (id: number) => {
    const updated = businessRoles.filter((item) => item.id !== id);
    form.setValue("businessRoles", updated);
  };

  return (
    <div className="mb-6">
      <div className="flex items-center gap-2">
        <span className="text-sm text-gray-500 dark:text-gray-400">
          Active filters:
        </span>
        <div className="flex flex-wrap gap-2">
          {trades.map((trade) => (
            <span
              className="bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200 px-3 py-1 rounded-full text-sm flex items-center gap-1"
              key={`trade-${trade.id}`}
            >
              {trade.name}
              <button
                className="ml-1 text-blue-600 dark:text-blue-300 hover:text-blue-800 dark:hover:text-blue-100"
                onClick={() => handleRemoveTrade(trade.id)}
              >
                ×
              </button>
            </span>
          ))}

          {categories.map((cat) => (
            <span
              className="bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200 px-3 py-1 rounded-full text-sm flex items-center gap-1"
              key={`category-${cat.id}`}
            >
              {cat.name}
              <button
                className="ml-1 text-blue-600 dark:text-blue-300 hover:text-blue-800 dark:hover:text-blue-100"
                onClick={() => handleRemoveCategory(cat.id)}
              >
                ×
              </button>
            </span>
          ))}

          {businessRoles.map((role) => (
            <span
              className="bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200 px-3 py-1 rounded-full text-sm flex items-center gap-1"
              key={`employee-role-${role.id}`}
            >
              {role.name}
              <button
                className="ml-1 text-blue-600 dark:text-blue-300 hover:text-blue-800 dark:hover:text-blue-100"
                onClick={() => handleRemoveEmployeeRole(role.id)}
              >
                ×
              </button>
            </span>
          ))}

          {isFavoriteOnly && (
            <span className="bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200 px-3 py-1 rounded-full text-sm flex items-center gap-1">
              <Star className="h-3 w-3 fill-current" />
              Favorites only
              <button
                className="ml-1 text-yellow-600 dark:text-yellow-300 hover:text-yellow-800 dark:hover:text-yellow-100"
                onClick={() => form.setValue("isFavoriteOnly", false)}
              >
                ×
              </button>
            </span>
          )}

          {isFeaturedOnly && (
            <span className="bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200 px-3 py-1 rounded-full text-sm flex items-center gap-1">
              <Star className="h-3 w-3 fill-current" />
              Featured only
              <button
                className="ml-1 text-purple-600 dark:text-purple-300 hover:text-purple-800 dark:hover:text-purple-100"
                onClick={() => form.setValue("isFeaturedOnly", false)}
              >
                ×
              </button>
            </span>
          )}

        </div>
        <button
          className="ml-2 text-sm text-blue-600 dark:text-blue-400 hover:underline"
          onClick={onClearAll}
        >
          Clear all
        </button>
      </div>
    </div>
  );
};

export default ActiveFiltersHandler;
